import { Typography } from '@mui/material';
import { useAnalytics } from '../../hooks/useAnalytics';
import GreenCornerBox from '../ui/GreenCornerBox';

/**
 * @typedef {Object} MarketplaceCTAProps
 * @property {string} [title] - CTA headline
 * @property {string} [description] - CTA description text
 * @property {string} [buttonText] - Button label
 * @property {string} [buttonLink] - Button destination
 * @property {string} [caption] - Optional caption text below button
 * @property {string} [ctaLocation] - Location identifier for analytics
 * @property {import('@mui/material').SxProps} [sx] - Additional MUI sx styling for the container
 */

/**
 * MarketplaceCTA - Reusable call-to-action component for marketplace sections
 * Features green corner styling and customizable content
 *
 * @param {MarketplaceCTAProps} props - Component props
 * @returns {React.ReactElement} Marketplace CTA component
 */
function MarketplaceCTA({
  title = '',
  description = '',
  buttonText = "Let's Discuss Your Project",
  buttonLink = '/discuss-project',
  caption = '',
  ctaLocation = 'marketplace',
  sx = {},
}) {
  const { trackCTA } = useAnalytics();

  // Check if the link is internal (starts with /)
  const isInternalLink = buttonLink.startsWith('/');

  /**
   * Handle CTA button click
   * @param {React.MouseEvent<HTMLAnchorElement>} e - Click event
   */
  const handleClick = (e) => {
    // Track the CTA click
    trackCTA(ctaLocation, buttonText, {
      destination: buttonLink,
      cta_title: title,
    });

    // For internal links, use window.location.href to force page reload (avoids reCAPTCHA conflicts)
    if (isInternalLink) {
      e.preventDefault();
      window.location.href = buttonLink;
    }
  };
  return (
    <GreenCornerBox
      maxWidth="none"
      sx={{
        backgroundColor: 'var(--color-card)',
        p: 4,
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        border: '1px solid var(--color-border)',
        textAlign: 'center',
        height: '220px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: '450px',
        mx: 'auto',
        // Override GreenCornerBox default corner size for consistency
        '&::before': {
          width: '50px',
          height: '50px',
        },
        '&::after': {
          width: '50px',
          height: '50px',
        },
        ...sx,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: 'var(--color-heading)',
          fontSize: '1.5rem',
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: 'var(--color-text-muted)',
          mb: 3,
          lineHeight: 1.4,
          fontSize: '1rem',
          maxWidth: '280px',
          mx: 'auto',
        }}
      >
        {description}
      </Typography>

      <Typography
        component="a"
        href={buttonLink}
        target={isInternalLink ? '_self' : '_blank'}
        rel={isInternalLink ? undefined : 'noopener noreferrer'}
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-primary-text)',
          textDecoration: 'none',
          textAlign: 'center',
          px: 3,
          py: 2,
          borderRadius: '8px',
          fontWeight: 600,
          fontSize: '1.1rem',
          transition: 'all 0.3s ease',
          boxShadow:
            '0 4px 12px color-mix(in srgb, var(--color-primary) 30%, transparent)',
          mx: 'auto',
          '&:hover': {
            backgroundColor: 'var(--color-primary-dark)',
            transform: 'translateY(-2px)',
            boxShadow:
              '0 6px 20px color-mix(in srgb, var(--color-primary) 40%, transparent)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
      >
        {buttonText}
      </Typography>

      {caption && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 2,
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          {caption}
        </Typography>
      )}
    </GreenCornerBox>
  );
}

export default MarketplaceCTA;
